import { Button, CheckBox, TextBox, Popup, SelectBox } from "devextreme-react";
import TabPanel, { Item as TabPanelItem } from "devextreme-react/tab-panel";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ThemeSettingsMenuTab } from "../tabs/menu";
import { ThemeSettingsTab } from "../tabs/grid";
import notify from "devextreme/ui/notify";
import { status_options, status_selectbox } from "../../../../shared/status-selectbox";
import { ISettingRequestParams } from "../../../../types/crm-contact";
import { sendNewSettingPayload } from "../../../../api/requests";
import { emptyThemeSettings, ThemeSettingsModel } from "../themeList";

interface SettingsPopupProps {
  selectedTheme?: ThemeSettingsModel | null;
  previewVisible: boolean;
  onHidingPreview: () => void;
  refresh: () => void;
  onApplyTheme: (newTheme: ThemeSettingsModel) => void;
}

export const ThemePopup = ({
  selectedTheme,
  previewVisible,
  onHidingPreview,
  refresh,
  onApplyTheme,
}: SettingsPopupProps) => {
  const { t } = useTranslation();

  const [popupSettingName, setPopupSettingName] = useState('');
  const [popupComment, setPopupComment] = useState('');
  const [popupIsDefault, setPopupIsDefault] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [localSettings, setLocalSettings] = useState<ThemeSettingsModel>(emptyThemeSettings);
  const [selectedStatus, setSelectedStatus] = useState<number>(1);
  const isNew = selectedTheme && !selectedTheme.settingRef;

  useEffect(() => {
    if (previewVisible && selectedTheme) {
      setPopupSettingName(selectedTheme.settingName || "");
      setPopupComment(selectedTheme.comment || "");
      setPopupIsDefault(selectedTheme.isDefault === "1" || selectedTheme.isDefault === 1);
      setSelectedStatus(selectedTheme.settingStatus ?? 1);
      // Deep copy: düzenlemeler orijinal veriyi etkilemesin diye
      setLocalSettings(parseThemeToLocalSettings(selectedTheme));
      // Yeni kayıt ise düzenleme modunu aç; mevcut kayıtta kapalı
      if (!selectedTheme.settingRef) {
        setIsEditable(true);
      } else {
        setIsEditable(false);
      }
    }
  }, [previewVisible, selectedTheme]);

  // selectedTheme'den localSettings'e dönüştürme fonksiyonu
  const parseThemeToLocalSettings = (theme: ThemeSettingsModel): ThemeSettingsModel => {
    // settingJson string'ini parse et ve tema ayarlarıyla birleştir
    let parsedSettings = emptyThemeSettings;
    if (theme.settingJson && theme.settingJson.trim() !== "") {
      try {
        const jsonSettings = JSON.parse(theme.settingJson);
        parsedSettings = { ...emptyThemeSettings, ...jsonSettings };
      } catch (error) {
        console.error("settingJson parse edilemedi:", error);
        parsedSettings = emptyThemeSettings;
      }
    }

    // Meta bilgileri de ekle ama tema ayarları öncelikli
    return {
      ...parsedSettings,
      settingRef: theme.settingRef,
      settingName: theme.settingName,
      comment: theme.comment,
      isDefault: theme.isDefault,
      settingStatus: theme.settingStatus,
    };
  };

  const statusChange = (e: any) => {
    const value = e.value;
    setSelectedStatus(value);
    updateField('settingStatus')(value);
  };

  const updateField = (key: keyof ThemeSettingsModel) => (value: any) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleEdit = () => {
    setIsEditable(true);
  };

  const handleCancel = () => {
    if (selectedTheme) {
      setLocalSettings(parseThemeToLocalSettings(selectedTheme));
      setPopupSettingName(selectedTheme.settingName || "");
      setPopupComment(selectedTheme.comment || "");
      setPopupIsDefault(selectedTheme.isDefault === "1" || selectedTheme.isDefault === 1);
      setIsEditable(false);
    }
  };

  const handleSave = () => {
    // Sadece tema ayarlarını serialize et, meta bilgileri hariç tut
    const themeSettings = {
      grid_row_padding: localSettings.grid_row_padding,
      grid_row_font: localSettings.grid_row_font,
      grid_row_bold: localSettings.grid_row_bold,
      grid_row_color: localSettings.grid_row_color,
      grid_row_fontfamily: localSettings.grid_row_fontfamily,
      grid_header_padding: localSettings.grid_header_padding,
      grid_header_font: localSettings.grid_header_font,
      grid_header_bold: localSettings.grid_header_bold,
      grid_header_color: localSettings.grid_header_color,
      grid_header_fontfamily: localSettings.grid_header_fontfamily,
      grid_row_alterneColor: localSettings.grid_row_alterneColor,
      menu_fontfamily: localSettings.menu_fontfamily,
      menu_fontsize: localSettings.menu_fontsize,
    };
    const settingJson = JSON.stringify(themeSettings);

    const currentSettingRef = selectedTheme?.settingRef || 0;
    const params: ISettingRequestParams = {
      settingRef: currentSettingRef,
      isDefault: popupIsDefault ? 1 : 0,
      settingStatus: selectedStatus,
      settingType: 2,
      settingPage: "theme",
      settingObject: "dx-datagrid-theme",
      settingName: popupSettingName,
      settingJson: settingJson,
      comment: popupComment,
    };
    console.log(popupIsDefault);

    sendNewSettingPayload(params)
      .then((resp) => {
        if (resp.resCode === 200) {
          notify(t("successfull"), "success");
          // Kaydet dedikten sonra temayı global olarak uygula
          onApplyTheme(localSettings);
          refresh();
          onHidingPreview();
          setTimeout(() => {
            window.location.reload();
          }, 100);
        } else {
          notify(resp.resMessage, "error");
        }
      })
      .catch((e) => {
        notify(e, "error");
      });

    setIsEditable(false);
  };

  return (
    <Popup
      visible={previewVisible}
      onHiding={onHidingPreview}
      resizeEnabled
      maxWidth="95%"
      width={800}
      height={600}
      maxHeight="95%"
      hideOnOutsideClick
      position={{ my: "center", at: "center", of: window, collision: "fit" }}
      titleRender={() => (
        <div className="flex flex-row justify-between items-center">
          <div className="flex items-center">
            <Button icon="close" stylingMode="text" onClick={onHidingPreview} />
            <p className="ml-2 mt-0 font-bold text-sm">{popupSettingName}</p>
          </div>

          <div className="flex flex-row gap-x-2">
            {!isEditable ? (
              <>
                <Button
                  text={t("edit")}
                  icon="edit"
                  stylingMode="contained"
                  type="default"
                  onClick={handleEdit}
                />
                <Button
                  text={t("close")}
                  icon="close"
                  stylingMode="contained"
                  type="default"
                  style={{ backgroundColor: "gray" }}
                  onClick={onHidingPreview}
                />
              </>
            ) : (
              <>
                <Button
                  text={t("save")}
                  icon="save"
                  stylingMode="contained"
                  type="default"
                  onClick={handleSave}
                />
                <Button
                  text={isNew ? t("close") : t("cancel")}
                  icon="close"
                  stylingMode="contained"
                  type="default"
                  style={{ backgroundColor: "gray" }}
                  onClick={isNew ? onHidingPreview : handleCancel}
                />
              </>
            )}
          </div>
        </div>
      )}
      contentRender={() => (
        <div className="flex w-full gap-x-4 flex-col lg:flex-row">

          <div className="flex-1 popup-grid-container w-full">
            <TabPanel defaultSelectedIndex={0}>
              <TabPanelItem title={t("general")}>
                <div className="w-full flex flex-col gap-y-2 pt-3">
                  <div className="flex justify-between items-center w-full">
                    <div className="pr-2">
                      <SelectBox
                        dataSource={status_options}
                        value={selectedStatus}
                        displayExpr={(item) => t(item.label)}
                        valueExpr="value"
                        onValueChanged={statusChange}
                        itemRender={status_selectbox}
                        searchEnabled={false}
                        readOnly={!isEditable}
                        className="text-xs"
                      />
                    </div>

                  </div>
                  <div className="flex flex-col gap-2 w-full md:w-96">
                    <TextBox
                      label={t("themeName")}
                      value={popupSettingName}
                      readOnly={!isEditable}
                      onValueChanged={(e) => setPopupSettingName(e.value)}
                    />
                    <TextBox
                      label={t("comment")}
                      value={popupComment}
                      readOnly={!isEditable}
                      onValueChanged={(e) => setPopupComment(e.value)}
                    />
                    <div className="mt-2">
                      <CheckBox
                        value={popupIsDefault}
                        text={t("default")}
                        onValueChanged={(e) => {
                          setPopupIsDefault(e.value);
                          updateField("isDefault")(e.value ? "1" : "0");
                        }}
                        readOnly={!isEditable}
                      />
                    </div>
                  </div>
                </div>
              </TabPanelItem>

              <TabPanelItem title={t("Grid")}>


                <ThemeSettingsTab
                  selectedTheme={selectedTheme}
                  isEditable={isEditable}
                  gridSettings={localSettings}
                  setGridSettings={setLocalSettings}
                />

              </TabPanelItem>

              <TabPanelItem title={t("Site")}>

                <ThemeSettingsMenuTab
                  isEditable={isEditable}
                  menuSettings={localSettings}
                  setMenuSettings={setLocalSettings}
                />
              </TabPanelItem>
            </TabPanel>
          </div>
        </div>
      )}
    ></Popup>
  );
};

export default ThemePopup;
